import { Avatar, Box, InputAdornment, styled, Typography } from '@mui/material';
import { useCreateBusiness } from 'apis/agency/hook';
import Button from 'components/Common/Button';
import Input from 'components/Common/Input';
import { useForm } from 'react-hook-form';
import Grid from '@mui/material/Grid2';
import { useState } from 'react';
import TelInput from 'components/Common/TelInput';

interface BusinessFormProps {
  handleClose: () => void;
}

export default function BusinessForm({ handleClose }: BusinessFormProps) {
  const { control, handleSubmit } = useForm();
  const [avatarUrl, setAvatarUrl] = useState<string | null>();
  const [avatarFile, setAvatarFile] = useState<File | null>();

  const { mutateAsync: createBusiness, isPending } = useCreateBusiness(handleClose);

  const onSubmit = (data: any) => {
    const formData = new FormData();
    Object.keys(data.business).forEach((key) => {
      formData.append(`business[${key}]`, data.business[key]);
    });
    if (avatarFile) {
      formData.append('business[avatar]', avatarFile);
    }

    createBusiness(formData);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const newUrl = URL.createObjectURL(file);
      setAvatarFile(file);
      setAvatarUrl(newUrl);
    }
  };

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)}>
      <Typography variant='h6' mb={3}>
        Business Information
      </Typography>
      <Box
        sx={{
          border: '1px solid #F3F3F3',
          borderRadius: '4px',
          padding: 2,
          mb: 3
        }}
      >
        <Typography variant='body1' fontWeight='500'>
          Business Logo
        </Typography>
        <Box
          display={{ xs: 'block', sm: 'flex' }}
          sx={{
            gap: 2,
            alignItems: 'center',
            marginTop: '8px'
          }}
        >
          <Avatar src={avatarUrl || '/path/to/default/avatar.png'} alt='User Avatar' sx={{ width: 64, height: 64 }} />
          <Box>
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Button component='label' role={undefined} variant='outlined' tabIndex={-1}>
                Choose files
                <VisuallyHiddenInput type='file' onChange={handleFileChange} multiple />
              </Button>
              <Typography variant='body2'>No file chosen</Typography>
            </Box>
            <Typography color='secondary' fontSize='12px'>
              The proposed size is 350px*180px. No bigger than 2.5MB{' '}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Grid container spacing={3}>
        <Grid size={6}>
          <Input label='Name' name='business.name' control={control} fullWidth />
        </Grid>
        <Grid size={6}>
          <Input label='Legal Name' name='business.legal_name' control={control} fullWidth />
        </Grid>
        <Grid size={6}>
          <Input label='Email' name='business.email' control={control} fullWidth />
        </Grid>
        <Grid size={6}>
          <TelInput
            name='business.phone'
            control={control}
            defaultCountry='US'
            preferredCountries={['US', 'CA', 'IN']}
            fullWidth
          />
        </Grid>
        <Grid size={6}>
          <Input
            label='Website'
            name='business.website'
            control={control}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position='start'>https://</InputAdornment>
              }
            }}
            fullWidth
          />
        </Grid>
        <Grid size={6}>
          <Input label='Niche' name='business.niche' control={control} fullWidth />
        </Grid>
      </Grid>
      <Box display='flex' justifyContent='end' gap={2} marginTop={3}>
        <Button color='secondary' variant='outlined' sx={{ border: '1px solid #F3F3F3' }} onClick={() => handleClose()}>
          Cancel
        </Button>
        <Button type='submit' isPending={isPending}>
          Submit
        </Button>
      </Box>
    </Box>
  );
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
});
